import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  FieldId,
  FormAlertService,
  GkKendoGridMapService,
  MapAction,
  MapId,
  MapObjectApiType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  PortalId,
  PzService,
  StreetService,
  ToolType,
  markFormGroupsTouched,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { FileSaverService } from 'ngx-filesaver';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  filter,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { ProxyDetailsComponent } from '../../../../components/proxy-details/proxy-details.component';
import { MainRoutes } from '../../../../guards/guard.models';
import { RequestInProgress } from '../../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../../shared/base-new-request/base-new-request.component';
import { InvestorDetailsComponent } from '../../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../../shared/request-reply-form/request-reply-form.component';
import { BusinessPurposesCheckboxControlComponent } from '../../../ownership-conversion-fee-calculation-request/business-purposes-checkbox-control/business-purposes-checkbox-control.component';
import { PARActType } from '../services/act-type/act-type.model';
import { PARActTypeService } from '../services/act-type/act-type.service';
import {
  PARLetterContentControlName,
  PARLetterContentFormGroup,
  PARLetterControlName,
  PARLetterDetailsControlName,
  PARLetterDetailsFormGroup,
  PARNewRequestControlName,
  PARNewRequestFormGroup,
} from '../services/new-request-form/new-request-form.model';
import { PARNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  PARLetterContent,
  PARLetterDetails,
  PARNewRequest,
  PARNewRequestDto,
  PARNewRequestFormWorkspaceState,
} from '../services/new-request/new-request.model';
import { PAROwnerService } from '../services/owner/owner.service';
import { PARRequestType } from '../services/request-type/request-type.model';
import { PARRequestTypeService } from '../services/request-type/request-type.service';

@Component({
  selector: 'app-lbrc-new-request',
  templateUrl: './new-request.component.html',
  standalone: false,
})
export class PARNewRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = PARNewRequestControlName;
  override formGroup: FormGroup<PARNewRequestFormGroup>;
  mapSettings: MapSettings;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  @ViewChild(BusinessPurposesCheckboxControlComponent)
  businessPurposesCheckboxControlComponent: BusinessPurposesCheckboxControlComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  successSigned = false;
  workspaceStateDraftVersion: WorkspaceStateDraftVersion;
  workspaceStateId: WorkspaceStateId;
  mainRoute: MainRoutes;
  addToSignUrl: string;
  requestPreviewUrl: string;
  letterContentWithParcelsControlName = PARLetterContentControlName;
  letterDetailsControlName = PARLetterDetailsControlName;
  isRequestTypeVisibe = false;
  private localToolTypes: ToolType[] = [ToolType.LandParcel];

  public readonly LETTER_CONTENT_WITH_PARCELS_GROUP =
    'letterContentWithParcels';
  public readonly LETTER_DETAILS_GROUP = 'letterDetails';

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private parNewRequestFormService: PARNewRequestFormService,
    public override streetService: StreetService,
    private toastr: ToastrService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
    protected override fileSaverService: FileSaverService,
    private activatedRoute: ActivatedRoute,
    public parActTypeService: PARActTypeService,
    public parRequestTypeService: PARRequestTypeService,
    public parOwnerService: PAROwnerService,
    public gkKendoGridMapService: GkKendoGridMapService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
      fileSaverService,
    );
  }

  override ngOnInit(): void {
    this.setPortalIdFromWebPortalResolver(this.activatedRoute.parent.snapshot);
    this.handlePortalType();
    this.fetchWrongFileText();
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
    this.setOwnerIfOneOwner();
  }

  setOwnerIfOneOwner(): void {
    this.parOwnerService
      .get(this.portalId)
      .pipe(
        takeWhile(() => this.isAlive),
        filter((owners) => owners.length === 1),
        map((owners) => owners[0]),
      )
      .subscribe((owner) => {
        this.getOwnerIdFormControl().setValue(owner.id);
      });
  }

  handlePortalType(): void {
    switch (this.portalId) {
      case PortalId.PlanningActCommentsRequest: {
        this.workspaceStateDraftVersion =
          WorkspaceStateDraftVersion.PlanningActCommentsRequestFormVersion;
        this.workspaceStateId = WorkspaceStateId.PlanningActCommentsRequestForm;
        this.mainRoute = MainRoutes.PlanningActCommentsRequest;
        this.addToSignUrl =
          '/api/interesant/wniosek/zgloszenieUwagDoKonsultowanegoProjektuAktuPlanowaniaPrzestrzennego/addToSign';
        this.requestPreviewUrl =
          '/api/interesant/wniosek/zgloszenieUwagDoKonsultowanegoProjektuAktuPlanowaniaPrzestrzennego/podglad';

        break;
      }
      case PortalId.PlanningActCreationAmendmentRequest: {
        this.workspaceStateDraftVersion =
          WorkspaceStateDraftVersion.PlanningActCreationAmendmentRequestFormVersion;
        this.workspaceStateId =
          WorkspaceStateId.PlanningActCreationAmendmentRequestForm;
        this.mainRoute = MainRoutes.PlanningActCreationAmendmentRequest;
        this.isRequestTypeVisibe = true;
        this.addToSignUrl =
          '/api/interesant/wniosek/zmianaSporzadzenieAktuPlanowaniaPrzestrzennego/addToSign';
        this.requestPreviewUrl =
          '/api/interesant/wniosek/zmianaSporzadzenieAktuPlanowaniaPrzestrzennego/podglad';

        break;
      }
      case PortalId.PlanningActProjectRequest: {
        this.workspaceStateDraftVersion =
          WorkspaceStateDraftVersion.PlanningActProjectRequestFormVersion;
        this.workspaceStateId = WorkspaceStateId.PlanningActProjectRequestForm;
        this.mainRoute = MainRoutes.PlanningActProjectRequest;
        this.addToSignUrl =
          '/api/interesant/wniosek/uwagiDoProjektuAktuPlanowaniaPrzestrzennego/addToSign';
        this.requestPreviewUrl =
          '/api/interesant/wniosek/uwagiDoProjektuAktuPlanowaniaPrzestrzennego/podglad';

        break;
      }

      default:
        break;
    }
  }

  createForm(): void {
    this.formGroup = this.parNewRequestFormService.getFormGroup({
      isRequestTypeVisibe: this.isRequestTypeVisibe,
    });
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.PlanningAct)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
      });
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty || this.attachmentsComponent.areChosenDocuments()
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid && this.attachmentsComponent.areDocumentsValid()
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(body, this.addToSignUrl),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getRequestBody = (): Observable<PARNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastr.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        PARNewRequest.fromAppToApi(
          this.getFormValue(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): PARNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
  });

  handleLoadWorkspaceResponse(state: PARNewRequestFormWorkspaceState): void {
    this.gkKendoGridMapService.$mapStates.next({});
    this.patchActTypeFormControlToPotentiallyActivateHiddenFields(
      state.formValue.actType,
    );
    this.createLetterDetailsFormArrayAccordingToWorkspaceState(
      state.formValue.letterDetails,
    );
    this.createLetterContentWithParcelsFormArrayAccordingToWorkspaceState(
      state.formValue.letterContentWithParcels,
    );
    this.changeDetectorRef.detectChanges();
    setTimeout(() => {
      this.formGroup.patchValue({
        ...state.formValue,
        investorDetails: {
          ...state.formValue.investorDetails,
          addressSelect: undefined,
        },
      });
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
      this.requestReplyFormComponent.patchRequestReplyFormControls({
        documentationForm: state.formValue.documentationForm,
        shippingForm: state.formValue.shippingForm,
      });
    }, 1000);
  }

  patchActTypeFormControlToPotentiallyActivateHiddenFields(
    value: PARActType,
  ): void {
    this.formGroup.patchValue({
      actType: value,
    });
  }

  createLetterDetailsFormArrayAccordingToWorkspaceState(
    letterDetails: PARLetterDetails[],
  ): void {
    this.getLetterDetailsFormArray()?.clear();
    letterDetails?.forEach((_, index) => {
      const newMapState = this.createMapState(index);

      this.getLetterDetailsFormArray().push(
        this.parNewRequestFormService.getLetterDetailsFormGroup(newMapState),
      );
      this.gkKendoGridMapService.initMapState(
        newMapState,
        this.LETTER_DETAILS_GROUP,
      );
    });
  }

  createLetterContentWithParcelsFormArrayAccordingToWorkspaceState(
    letterContentWithParcels: PARLetterContent[],
  ): void {
    this.getLetterContentWithParcelsFormArray()?.clear();
    letterContentWithParcels?.forEach(() => {
      this.addLetterContentWithParcelsFormControl();
    });
  }

  getFormValue(): PARNewRequest {
    return {
      ...this.formGroup.getRawValue(),
      ...(this.isLetterDetailsSectionShouldBeVisible()
        ? {}
        : {
            letterDetails: [],
          }),
    };
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(`/${this.mainRoute}/requests-list`);
  }

  isRequestTypeFormControlValid(): boolean {
    return this.getRequestTypeFormControl().valid;
  }

  getRequestTypeFormControl(): FormControl<PARRequestType> {
    return this.formGroup.get(
      PARNewRequestControlName.RequestType,
    ) as FormControl<PARRequestType>;
  }

  isActTypeFormControlValid(): boolean {
    return this.getActTypeFormControl().valid;
  }

  getActTypeFormControl(): FormControl<PARActType> {
    return this.formGroup.get(
      PARNewRequestControlName.ActType,
    ) as FormControl<PARActType>;
  }

  isLetterContentFormControlValid(): boolean {
    return this.getLetterContentFormControl().valid;
  }

  getLetterContentFormControl(): FormControl<string> {
    return this.formGroup.get(
      PARNewRequestControlName.LetterContent,
    ) as FormControl<string>;
  }

  isLetterContentWithParcelsFormGroupValid(index: number): boolean {
    const letterContentWithParcelsFormGroup =
      this.getLetterContentWithParcelsFormArray().at(index);
    return letterContentWithParcelsFormGroup.valid;
  }

  deleteLetterContentWithParcelsFormControl(index: number): void {
    this.getLetterContentWithParcelsFormArray().removeAt(index);
    this.gkKendoGridMapService.removeMapState(
      index,
      this.LETTER_CONTENT_WITH_PARCELS_GROUP,
    );
    this.gkKendoGridMapService.reindexMapStates(
      this.LETTER_CONTENT_WITH_PARCELS_GROUP,
    );
  }

  handleLetterContentWithParcelsMapAction(
    mapAction: MapAction,
    index: number,
  ): void {
    this.gkKendoGridMapService.setActiveMapStateKey(
      this.LETTER_CONTENT_WITH_PARCELS_GROUP,
    );
    const formControl =
      this.getLetterContentWithParcelsMapStateFormControl(index);
    const currentMapState = formControl.value;

    const activeToolType = this.getActiveToolType(currentMapState);
    const isAuxiliaryTool =
      activeToolType && currentMapState.toolsState[activeToolType]?.isAuxiliary;

    const isLocalToolTypeMapAction = this.localToolTypes.includes(
      mapAction.payload?.options?.toolType as ToolType,
    );

    if (isLocalToolTypeMapAction || !isAuxiliaryTool) {
      formControl.setValue(
        this.mapStateService.getUpdatedMapState(mapAction, currentMapState),
      );
      this.gkKendoGridMapService.$mapState.next(currentMapState);
      this.gkKendoGridMapService.dispatchToMultipleMaps(
        mapAction,
        index,
        this.LETTER_CONTENT_WITH_PARCELS_GROUP,
      );
    } else {
      this.gkKendoGridMapService.dispatchToMultipleMaps(
        mapAction,
        index,
        this.LETTER_CONTENT_WITH_PARCELS_GROUP,
      );
      this.gkKendoGridMapService.$pendingMapActions.next([mapAction]);
    }
  }

  isSomeParcelsForLetterContentWithParcelsChosen(index: number): boolean {
    return this.getLetterContentWithParcelsMapObjectsTableState(
      index,
    ).mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  getLetterContentWithParcelsMapObjectsTableState(
    index: number,
  ): MapObjectTableState {
    return this.getLetterContentWithParcelsMapStateFormControl(index).value
      .mapObjectTablesState[0];
  }

  getLetterContentWithParcelsMapStateFormControl(
    index: number,
  ): FormControl<MapState> {
    return this.getLetterContentWithParcelsFormArray()
      .at(index)
      .get(PARLetterControlName.MapState) as FormControl<MapState>;
  }

  getLetterContentWithParcelsActNameFormControl(
    index: number,
  ): FormControl<string> {
    return this.getLetterContentWithParcelsFormArray()
      .at(index)
      .get(PARLetterContentControlName.ActName) as FormControl<string>;
  }

  getLetterContentWithParcelsContentFormControl(
    index: number,
  ): FormControl<string> {
    return this.getLetterContentWithParcelsFormArray()
      .at(index)
      .get(PARLetterContentControlName.Content) as FormControl<string>;
  }

  addLetterContentWithParcelsFormControl(): void {
    const mapIndex =
      this.gkKendoGridMapService.$mapStates.getValue()[
        this.LETTER_CONTENT_WITH_PARCELS_GROUP
      ]?.length ?? 0;
    const newMapState = this.createMapState(mapIndex);

    this.getLetterContentWithParcelsFormArray().push(
      this.parNewRequestFormService.getLetterContentWithParcelsFormGroup(
        newMapState,
      ),
    );
    this.gkKendoGridMapService.initMapState(
      newMapState,
      this.LETTER_CONTENT_WITH_PARCELS_GROUP,
    );
  }

  addLetterDetailsFormControl(): void {
    const mapIndex =
      this.gkKendoGridMapService.$mapStates.getValue()[
        this.LETTER_DETAILS_GROUP
      ]?.length ?? 0;
    const newMapState = this.createMapState(mapIndex);

    this.getLetterDetailsFormArray().push(
      this.parNewRequestFormService.getLetterDetailsFormGroup(newMapState),
    );
    this.gkKendoGridMapService.initMapState(
      newMapState,
      this.LETTER_DETAILS_GROUP,
    );
  }

  createMapState(mapIndex: number): MapState {
    return new MapState(
      MapId.PlanningAct,
      this.mapStateService.getViewState(
        MapId.PlanningAct,
        this.mapSettings,
        false,
        null,
        false,
        true,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel, ToolType.DevelopmentPlan],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
        mapIndex,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.PlanningAct,
        this.mapSettings,
        MapPortalName.PlanningAct,
      ),
      this.createMapObjectTableState(),
    );
  }

  createMapObjectTableState(): MapObjectTableState[] {
    return [
      new MapObjectTableState(
        [
          new ColumnHeader(
            'mapObjectNumber',
            'PARCEL_SEARCH_FORM.PARCEL_NUMBER',
          ),
          new ColumnHeader('districtName', 'PARCEL_SEARCH_FORM.DISTRICT'),
          new ColumnHeader('mapSheet', 'PARCEL_SEARCH_FORM.SHEET'),
        ],
        undefined,
        this.defaultMapGeometryStyles,
        true,
        false,
        true,
        '25',
      ),
    ];
  }

  getLetterContentWithParcelsFormArray(): FormArray<
    FormGroup<PARLetterContentFormGroup>
  > {
    return this.formGroup.get(
      PARNewRequestControlName.LetterContentWithParcels,
    ) as FormArray<FormGroup<PARLetterContentFormGroup>>;
  }

  isLetterDetailsFormGroupValid(index: number): boolean {
    const letterContentWithParcelsFormGroup =
      this.getLetterDetailsFormArray().at(index);
    return letterContentWithParcelsFormGroup.valid;
  }

  deleteLetterDetailsFormControl(index: number): void {
    this.getLetterDetailsFormArray().removeAt(index);
    this.gkKendoGridMapService.removeMapState(index, this.LETTER_DETAILS_GROUP);
    this.gkKendoGridMapService.reindexMapStates(this.LETTER_DETAILS_GROUP);
  }

  handleLetterDetailsMapAction(mapAction: MapAction, index: number): void {
    this.gkKendoGridMapService.setActiveMapStateKey(this.LETTER_DETAILS_GROUP);
    const formControl = this.getLetterDetailsMapStateFormControl(index);
    const currentMapState = formControl.value;
    const activeToolType = this.getActiveToolType(currentMapState);
    const isAuxiliaryTool =
      activeToolType && currentMapState.toolsState[activeToolType]?.isAuxiliary;

    const isLocalToolTypeMapAction = this.localToolTypes.includes(
      mapAction.payload?.options?.toolType as ToolType,
    );

    if (isLocalToolTypeMapAction || !isAuxiliaryTool) {
      formControl.setValue(
        this.mapStateService.getUpdatedMapState(mapAction, currentMapState),
      );
      this.gkKendoGridMapService.$mapState.next(currentMapState);
      this.gkKendoGridMapService.dispatchToMultipleMaps(
        mapAction,
        index,
        this.LETTER_DETAILS_GROUP,
      );
    } else {
      this.gkKendoGridMapService.dispatchToMultipleMaps(
        mapAction,
        index,
        this.LETTER_DETAILS_GROUP,
      );
      this.gkKendoGridMapService.$pendingMapActions.next([mapAction]);
    }
  }

  isSomeParcelsForLetterDetailsChosen(index: number): boolean {
    return this.getLetterDetailsMapObjectsTableState(index).mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  getLetterDetailsMapObjectsTableState(index: number): MapObjectTableState {
    return this.getLetterDetailsMapStateFormControl(index).value
      .mapObjectTablesState[0];
  }

  getLetterDetailsMapStateFormControl(index: number): FormControl<MapState> {
    return this.getLetterDetailsFormArray()
      .at(index)
      .get(PARLetterControlName.MapState) as FormControl<MapState>;
  }

  getLetterDetailsActNameFormControl(index: number): FormControl<string> {
    return this.getLetterDetailsFormArray()
      .at(index)
      .get(PARLetterDetailsControlName.ActName) as FormControl<string>;
  }

  getLetterDetailsLandUseClassFormControl(
    index: number,
  ): FormControl<FieldId[]> {
    return this.getLetterDetailsFormArray()
      .at(index)
      .get(PARLetterDetailsControlName.LandUseClass) as FormControl<FieldId[]>;
  }

  getLetterDetailsMaxBuildingCoverageFormControl(
    index: number,
  ): FormControl<number> {
    return this.getLetterDetailsFormArray()
      .at(index)
      .get(
        PARLetterDetailsControlName.MaxBuildingCoverage,
      ) as FormControl<number>;
  }

  getLetterDetailsMaxBuildingHeightFormControl(
    index: number,
  ): FormControl<number> {
    return this.getLetterDetailsFormArray()
      .at(index)
      .get(
        PARLetterDetailsControlName.MaxBuildingHeight,
      ) as FormControl<number>;
  }

  getLetterDetailsMinBiologicallyActiveAreaFormControl(
    index: number,
  ): FormControl<number> {
    return this.getLetterDetailsFormArray()
      .at(index)
      .get(
        PARLetterDetailsControlName.MinBiologicallyActiveArea,
      ) as FormControl<number>;
  }

  getLetterDetailsFormArray(): FormArray<FormGroup<PARLetterDetailsFormGroup>> {
    return this.formGroup.get(
      PARNewRequestControlName.LetterDetails,
    ) as FormArray<FormGroup<PARLetterDetailsFormGroup>>;
  }

  isLetterDetailsSectionShouldBeVisible(): boolean {
    const requestTypeFormControlValue = this.getRequestTypeFormControl()?.value;
    const actTypeFormControlValue = this.getActTypeFormControl()?.value;
    const isProperRequestType =
      !this.isRequestTypeVisibe ||
      requestTypeFormControlValue === PARRequestType.ActAmendment;
    const isProperActType =
      actTypeFormControlValue === PARActType.CommunityGeneralPlan ||
      actTypeFormControlValue === PARActType.DevelopmentPlan;
    const shouldBeVisible = isProperRequestType && isProperActType;

    const letterDetailsFormArray = this.getLetterDetailsFormArray();
    if (!shouldBeVisible && letterDetailsFormArray.enabled) {
      letterDetailsFormArray.disable();
    } else if (shouldBeVisible && letterDetailsFormArray.disabled) {
      letterDetailsFormArray.enable();
    }

    return shouldBeVisible;
  }

  isOwnerIdFormControlValid(): boolean {
    return this.getOwnerIdFormControl().valid;
  }

  getOwnerIdFormControl(): FormControl<FieldId> {
    return this.formGroup.get(
      PARNewRequestControlName.OwnerId,
    ) as FormControl<FieldId>;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
